import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrderContext } from '@/modules/order/context/OrderContext.tsx';
import { useNavigate } from 'react-router';
import { useSubmitOrderV2 } from '@/clients/order/useSubmitOrderV2.ts';
import { constructOrderConfiguration } from '@/modules/order/components/OrderSummary/utils/constructOrderConfiguration.ts';
import { useMarketContext } from '@/shared/context/MarketContext.tsx';
import { usePlatformOrganizationId } from '@/clients/platformInfo/usePlatformInfo.ts';
import { FinanceSettings } from '@/shared/models/ContextDataModel/interfaces/entities/finance-settings.ts';
import { FinanceSettingsForm } from '@/shared/forms/FinanceSettingsForm/FinanceSettingsForm.tsx';

export const OrderSummary: React.FC = () => {
    const { mutateAsync: mutateAsyncSubmitOrderV2, isPending, isSuccess } = useSubmitOrderV2();
    const { contextData, isPositionOrder, position } = useOrderContext();
    const { marketCode } = useMarketContext();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const platformOrganizationId = usePlatformOrganizationId();

    const productConfigurations = contextData.getProductConfigurations();
    const customerInfo = contextData.getCustomerInformation();
    const businessCase = contextData.getBusinessCase();
    const financeSettings = contextData.getFinanceSettings();

    if (!customerInfo || !productConfigurations || !businessCase) {
        throw new Error('customerInfo, productConfigurations, businessCase are needed to render OrderSummary');
    }

    const handleSubmit = useCallback(
        async (financeSettings: FinanceSettings[]) => {
            try {
                contextData.setFinanceSettings(financeSettings);

                const orderConfiguration = constructOrderConfiguration({
                    customerInfo,
                    businessCase,
                    financeSettings,
                    productConfigurations,
                    marketCode,
                    platformOrganizationId,
                    position,
                    source: isPositionOrder ? 'GENERIC_POSITION' : 'GENERIC_ORDER',
                });

                const response = await mutateAsyncSubmitOrderV2(orderConfiguration);
                void navigate(`/order/${response.id}/confirmation`);
            } catch (error) {
                console.error(error);
            }
        },
        [
            businessCase,
            contextData,
            customerInfo,
            isPositionOrder,
            marketCode,
            mutateAsyncSubmitOrderV2,
            navigate,
            platformOrganizationId,
            position,
            productConfigurations,
        ]
    );

    return (
        <FinanceSettingsForm
            customerOrganizationId={customerInfo.organizationId}
            defaultValues={financeSettings}
            productConfigurations={productConfigurations}
            isSubmissionSuccessful={isSuccess}
            isSubmissionPending={isPending}
            onSubmit={handleSubmit}
            onChange={financeSetting => {
                contextData.upsertFinanceSetting(financeSetting);
            }}
            submitButtonLabel={t('sections.summary.buttons.createOrder')}
            submitButtonDataTest="order-confirmation-button"
            mode="create"
        />
    );
};
